import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Plus,
  Trash2,
  Share2,
  Eye,
  Calendar,
  Loader2,
  X,
} from 'lucide-react';
import { getErrorMessage } from '../../services/api';

/* ─── Types ─── */
interface Report {
  id: string;
  user_id: string;
  name: string;
  share_token: string;
  date_from: string;
  date_to: string;
  platforms: string[];
  is_active: boolean;
  view_count: number;
  created_at: string;
  expires_at: string | null;
}

interface CreateReportRequest {
  name: string;
  date_from: string;
  date_to: string;
  platforms: string[];
  branding?: { logo_url?: string; company_name?: string } | null;
}

/* ─── Constants ─── */
const PLATFORMS = [
  'facebook',
  'instagram',
  'twitter',
  'linkedin',
  'tiktok',
  'youtube',
  'pinterest',
  'threads',
];

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter / X',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  threads: 'Threads',
};

/* ─── Hooks for API calls ─── */
const useReportsAPI = () => {
  const baseUrl = import.meta.env.VITE_API_URL || '';

  return {
    createReport: async (data: CreateReportRequest) => {
      const response = await fetch(`${baseUrl}/api/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json();
    },

    listReports: async () => {
      const response = await fetch(`${baseUrl}/api/reports`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json();
    },

    deleteReport: async (reportId: string) => {
      const response = await fetch(`${baseUrl}/api/reports/${reportId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (!response.ok) throw new Error(await response.text());
    },
  };
};

/* ─── Create Report Modal ─── */
interface CreateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateReportRequest) => Promise<void>;
  isLoading: boolean;
}

const CreateReportModal: React.FC<CreateReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateReportRequest>({
    name: '',
    date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
    platforms: [],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error(t('reports_name_required', 'Report name is required'));
      return;
    }
    try {
      await onSubmit(formData);
      setFormData({
        name: '',
        date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
        date_to: new Date().toISOString().split('T')[0],
        platforms: [],
      });
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, t('reports_create_error', 'Failed to create report')));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('reports_create_title', 'Create Report')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reports_name_label', 'Report Name')}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder={t('reports_name_placeholder', 'e.g., January 2026 Report')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-800 dark:text-white text-sm"
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reports_from_date', 'From')}
              </label>
              <input
                type="date"
                value={formData.date_from}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, date_from: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-800 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reports_to_date', 'To')}
              </label>
              <input
                type="date"
                value={formData.date_to}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, date_to: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-800 dark:text-white text-sm"
              />
            </div>
          </div>

          {/* Platforms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('reports_platforms_label', 'Platforms (Optional)')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PLATFORMS.map((platform) => (
                <label
                  key={platform}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.platforms.includes(platform)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData((prev) => ({
                          ...prev,
                          platforms: [...prev.platforms, platform],
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          platforms: prev.platforms.filter((p) => p !== platform),
                        }));
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {PLATFORM_LABELS[platform]}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              {t('common_cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t('common_creating', 'Creating...')}
                </>
              ) : (
                <>
                  <Plus size={16} />
                  {t('reports_create_button', 'Create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Main Component ─── */
const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const api = useReportsAPI();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['reports'],
    queryFn: api.listReports,
  });

  const createMutation = useMutation({
    mutationFn: api.createReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success(t('reports_created_success', 'Report created successfully'));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('reports_create_error', 'Failed to create report')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success(t('reports_deleted_success', 'Report deleted'));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('reports_delete_error', 'Failed to delete report')));
    },
  });

  const copyShareLink = (report: Report) => {
    const shareUrl = `${window.location.origin}/report/${report.share_token}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success(t('reports_link_copied', 'Share link copied to clipboard'));
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('reports_page_title', 'Reports')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t(
              'reports_page_subtitle',
              'Create and share analytics reports with clients'
            )}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus size={20} />
          {t('reports_create_button', 'Create Report')}
        </button>
      </div>

      {/* Create Modal */}
      <CreateReportModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={(data) => createMutation.mutateAsync(data)}
        isLoading={createMutation.isPending}
      />

      {/* Reports List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-gray-400" />
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 py-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('reports_no_reports', 'No reports yet')}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            {t('reports_create_first', 'Create your first report')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {reports.map((report: Report) => (
            <div
              key={report.id}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:shadow-md transition"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {report.name}
                  </h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      {formatDate(report.date_from)} -{' '}
                      {formatDate(report.date_to)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye size={16} />
                      {t('reports_views', '{{count}} views', {
                        count: report.view_count,
                      })}
                    </div>
                  </div>
                  {report.platforms.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {report.platforms.map((platform) => (
                        <span
                          key={platform}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                        >
                          {PLATFORM_LABELS[platform]}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyShareLink(report)}
                    title={t('reports_copy_link', 'Copy share link')}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition"
                  >
                    <Share2 size={18} />
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(report.id)}
                    disabled={deleteMutation.isPending}
                    title={t('common_delete', 'Delete')}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition disabled:opacity-50"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
